import { useEffect, useState } from 'react';
import { useCredits } from './useCredits';

export type CostDisplayMode = 'credits' | 'usd';

const STORAGE_KEY = 'admin.costDisplay';
const CHANGE_EVENT = 'cost-display-change';

function readStored(): CostDisplayMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === 'usd' ? 'usd' : 'credits';
  } catch {
    return 'credits';
  }
}

export function setCostDisplay(mode: CostDisplayMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: mode }));
  } catch {
    // ignore storage errors
  }
}

// Non-admin callers always see credits regardless of the stored flag.
// Admin callers can toggle via setCostDisplay(); all call sites using
// the hook re-render through a window CustomEvent.
export function useCostDisplay(): CostDisplayMode {
  const { isAdmin } = useCredits();
  const [stored, setStored] = useState<CostDisplayMode>(readStored);

  useEffect(() => {
    const handler = () => setStored(readStored());
    window.addEventListener(CHANGE_EVENT, handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener(CHANGE_EVENT, handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  return isAdmin ? stored : 'credits';
}
